import './PlanSelection.scss';

import { Capacitor } from '@capacitor/core';
import {
  Country,
  getCurrencyCode,
  getFeaturedItemAndTextForTracking,
  Icon,
  isDefaultBrand,
  PageSpinner,
  ProductOffering,
  sortProductOfferingsBy,
  Title,
  useConfig,
} from '@travelwin/core';
import { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  HelpCentre,
  PlanSelectionCountry,
  TestimonialItem,
} from '@/interfaces/cms';

import {
  SearchEventData,
  SelectItemEventData,
  useAnalytics,
  ViewItemListEventData,
} from '../../analytics';
import planSelectionHero from '../../assets/plan-selection.svg';
import EsimDestinationTypeahead from '../../components/EsimBundleSearchForm/EsimBundleSearchForm';
import Filter from '../../components/Filter/Filter';
import Footer from '../../components/Footer';
import HelmetWrapper from '../../components/HelmetWrapper';
import { ImageMedia } from '../../components/ImageMedia';
import { Layout } from '../../components/Layout';
import { useNavigate } from '../../components/Link';
import PlanCard from '../../components/PlanCard';
import { RichText } from '../../components/RichText';
import { ShowTo } from '../../components/ShowTo';
import { SortOption } from '../../components/Sort/Sort';
import {
  GOOGLE_ICON,
  TRIPADVISOR_ICON,
  TRIPADVISOR_RATING_URL,
} from '../../components/TrustedBy/TrustedBy';
import { useBuyPlanHandler } from '../../hooks/useBuyPlanHandler';
import { useCountryUtils } from '../../hooks/useCountryUtils';
import useFilteredProducts from '../../hooks/useFilteredProducts';
import { useLocaleUtils } from '../../hooks/useLocaleUtils';
import {
  FeaturedCountry,
  getViewerCountryCode,
} from '../../services/resources/ResourceService';
import { PlanSelectionContent } from './components';
import { useCountryDescriptions } from './CountryDescriptions';

const isBrowserPlatform = !Capacitor.isNativePlatform();

const className = 'p-PlanSelection';

type Props = {
  countries: Country[];
  currentCountry: Country;
  products?: ProductOffering[];
  isLoading?: boolean;
  helpCentre?: HelpCentre;
  cmsContent?: PlanSelectionCountry;
  featuredCountries: FeaturedCountry[];
  userAgent: string;
  testimonials?: TestimonialItem[];
  plans?: ProductOffering[];
};

const sortProducts = (
  sortBy: string,
  productsToSort: ProductOffering[] | undefined,
) => {
  if (!productsToSort) {
    return;
  }
  const plans = sortProductOfferingsBy(sortBy, productsToSort);
  if (!plans) {
    return [];
  }
  return [
    ...plans.featured,
    ...plans.local,
    ...plans.regional,
    ...plans.global,
  ];
};

const PlanSelection: FC<Props> = ({
  countries,
  currentCountry,
  products,
  isLoading,
  helpCentre,
  cmsContent,
  featuredCountries,
  userAgent,
  testimonials,
  plans,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(
    currentCountry,
  );
  const { WHITELABEL_BRAND, PLAN_SELECTION_DESTINATION_SEARCH_ENABLED } =
    useConfig();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLocale } = useLocaleUtils();
  const analyticsContext = useAnalytics();
  const countryDescriptions = useCountryDescriptions();
  const { getCountryNameTranslation, getCountryName } = useCountryUtils();

  const [sort, setSort] = useState<SortOption>('default');

  const [searchParams] = useSearchParams();

  const countriesWithActiveOfferings = countries.filter(
    (c) => c.has_active_offerings,
  );
  const categories = {
    [t('categories.plan', 'Plan')]: ['data-only', 'data-calls-texts'],
    [t('categories.data', 'Data')]: ['Unlimited'],
    [t('categories.planLength', 'Days')]: [
      'any-length',
      '1to7Days',
      '8to30Days',
      '31PlusDays',
    ],
    [t('categories.price', 'Price')]: ['anyPrice', '0to20', '20To40', '40Plus'],
  };

  const currency =
    getCurrencyCode(
      currentLocale,
      (
        products?.[0]?.product_offering_pricing[0].currency.alias ?? 'EUR'
      ).toUpperCase(),
    ) ?? '€';

  const categoryDisplayNames: Record<string, string> = {
    'data-only': t('categories.dataOnly', 'Data Only'),
    'data-calls-texts': t('categories.dataCallsTexts', 'Data/Calls/Texts'),
    Unlimited: t('categories.unlimited', 'Unlimited Plans'),
    'any-length': t('categories.anyLength', 'All'),
    '1to7Days': t('categories.lessThan7Days', '1–7 Days'),
    '8to30Days': t('categories.lessThan30Days', '8–30 Days'),
    '31PlusDays': t('categories.60PlusDays', '31+ Days'),
    anyPrice: t('categories.anyPrice', 'All'),
    '0to20': t('categories.0to20', '{{currency}}0 - {{currency}}20', {
      currency,
    }),
    '20To40': t('categories.20To40', '{{currency}}20 - {{currency}}40', {
      currency,
    }),
    '40Plus': t('categories.30Plus', '{{currency}}40+', { currency }),
  };

  useEffect(() => {
    if (products && products?.length > 0) {
      const trackingData: ViewItemListEventData = {
        items: [
          ...products.map((product: ProductOffering) => {
            return {
              id: product.id,
              name: product.name,
              retailerId: product.retailer.id,
              brand: product.mvno.alias,
              countryName: currentCountry.english_name,
              price: product.product_offering_pricing[0].total_price_gross,
              currency:
                product.product_offering_pricing[0].currency.alias.toUpperCase(),
              quantity: 1,
              ...getFeaturedItemAndTextForTracking(product),
            };
          }),
        ],
      };

      analyticsContext?.trackViewItemList(trackingData);
    }
  }, [products]);

  useEffect(() => {
    setSelectedCountry(currentCountry);
  }, [currentCountry]);

  const handlePlanSelect = useBuyPlanHandler();

  const filteredProducts = useFilteredProducts(products || [], searchParams);
  const sortedFilteredProducts = sortProducts(sort, filteredProducts);

  const getCountryDescription = (country: string) => {
    if (cmsContent) {
      return <RichText content={cmsContent.subText} />;
    }
    if (country in countryDescriptions && isDefaultBrand(WHITELABEL_BRAND)) {
      return (
        <>{countryDescriptions[country as keyof typeof countryDescriptions]}</>
      );
    } else {
      return (
        <Trans
          i18nKey="plan_selection.destination_subtitle"
          defaults="Connect instantly with an eSIM and start making the most of your trip. <br /> Because when you travel, every moment counts."
        />
      );
    }
  };

  const handleCountryChange = (country: Country | null) => {
    setSelectedCountry(country);

    if (!country) return;

    if (country.id !== currentCountry.id) {
      navigate(`/plan-selection/${country.slug}`);

      getViewerCountryCode().then((viewerCountryCode) => {
        const trackingData: SearchEventData = {
          searchClick: 'search box',
          countryName: country.english_name,
          userDestination: getCountryName(`countries.${viewerCountryCode}`),
        };

        analyticsContext?.trackSearch(trackingData);
      });
    }
  };

  const handleProductDetailClick = (product: ProductOffering) => {
    if (products && products?.length > 0) {
      const trackingData: SelectItemEventData = {
        currency:
          product.product_offering_pricing[0].currency.alias.toUpperCase(),
        price: product.product_offering_pricing[0].total_price_gross,
        items: [
          {
            id: product.id,
            name: product.name,
            brand: product.mvno.alias,
            quantity: 1,
            retailerId: product.retailer.id,
            countryName: currentCountry.english_name,
            itemListName: 'n/a',
            price: product.product_offering_pricing[0].total_price_gross,
            currency:
              product.product_offering_pricing[0].currency.alias.toUpperCase(),
            ...getFeaturedItemAndTextForTracking(product),
          },
        ],
      };

      analyticsContext?.trackSelectItem(trackingData);
    }

    navigate(`/esim${product.path}`, {
      state: {
        analyticsData: {
          countryName: currentCountry.english_name,
        },
      },
    });
  };

  const renderPlans = () => {
    if (isLoading) {
      return <PageSpinner />;
    }
    if (!sortedFilteredProducts?.length) {
      return <EmptyResult />;
    }
    return (
      <div className={`${className}__plans-container`}>
        {sortedFilteredProducts?.map((product) => (
          <PlanCard
            key={product.id}
            planDetails={product}
            cardClickable={true}
            onBuyNowClick={() =>
              handlePlanSelect({
                product,
                countryName: currentCountry.english_name,
              })
            }
            onProductDetailsClick={() => handleProductDetailClick(product)}
            onCardClick={() => handleProductDetailClick(product)}
            showSubtitle={true}
          />
        ))}
      </div>
    );
  };

  return (
    <Layout zendesk="show">
      <div className={className}>
        {typeof window !== 'undefined' && (
          <HelmetWrapper
            title={t('browser.planSelectionTitle', 'eSIM {{country}}', {
              country: getCountryNameTranslation(currentCountry.description),
            })}
          />
        )}
        <div className={`${className}__destination-section`}>
          <div className={`${className}__destination-header`}>
            <section>
              <Title>
                {t('plan_selection.destination_title', 'eSIM {{destination}}', {
                  destination: getCountryNameTranslation(
                    currentCountry.description,
                  ),
                })}
              </Title>
              <div className={`${className}__destination-description`}>
                {getCountryDescription(currentCountry.id)}
              </div>
            </section>
            {PLAN_SELECTION_DESTINATION_SEARCH_ENABLED && (
              <div className={`${className}__destination-typeahead`}>
                <EsimDestinationTypeahead
                  className={className}
                  countries={countriesWithActiveOfferings}
                  selectedCountry={selectedCountry}
                  onChange={handleCountryChange}
                />
              </div>
            )}
            <ShowTo brand="simlocal">
              <div className={`${className}__tagline-reviews`}>
                <a
                  aria-label={t('trusted_by.tripadvisor', 'TripAdvisor')}
                  href={TRIPADVISOR_RATING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name={TRIPADVISOR_ICON} color="color-neutral-7" />
                </a>
                <Icon name={GOOGLE_ICON} color="color-neutral-7" />
              </div>
            </ShowTo>
          </div>
          <div
            className={`${className}__destination-big-sim-image --mobile-hide`}
          >
            {cmsContent?.graphic ? (
              <ImageMedia
                media={cmsContent.graphic || 0}
                className={`${className}__hero`}
                sizes="(min-width: 768px) 50vw, 100vw"
                fetchpriority="high"
              />
            ) : (
              <img alt="esim" src={planSelectionHero} role="presentation" />
            )}
          </div>
        </div>

        <div className={`${className}__page-content-narrow`}>
          <Filter
            categories={categories}
            displayNames={categoryDisplayNames}
            onSortChange={(sort) => setSort(sort)}
            userAgent={userAgent}
          >
            <div className={`${className}__products-container`}>
              {renderPlans()}
            </div>
          </Filter>
        </div>
      </div>

      <PlanSelectionContent
        cmsContent={cmsContent}
        plans={plans}
        testimonials={testimonials}
        helpCentre={helpCentre}
      />

      {isBrowserPlatform && (
        <Footer countries={countries} featuredCountries={featuredCountries} />
      )}
    </Layout>
  );
};

const EmptyResult = () => {
  const { t } = useTranslation();
  return (
    <div className={`${className}__empty-plans__container`}>
      <div className={`${className}__empty-plans__icon-container`}>
        <Icon name="simOutline" />
      </div>
      <div className={`${className}__empty-plans__title`}>
        {t('plan_selection.empty_plans_title', 'Not a match...')}
      </div>
      <div className={`${className}__empty-plans__help-text`}>
        <p>
          {t(
            'plan_selection.empty_plans_help_text_line1',
            "We can't find a match for your selection.",
          )}
        </p>
        <p>
          {t(
            'plan_selection.empty_plans_help_text_line2',
            "Try resetting your filters or searching again to see what's on offer.",
          )}
        </p>
      </div>
    </div>
  );
};

export default PlanSelection;
